import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaCheck, FaTimes, FaEye, FaUser, FaClock, FaFileAlt, FaDownload, FaUsers, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";

const AcceptedByReporterPost = () => {
  const [ads, setAds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Pagination state for ads list
  const [currentPage, setCurrentPage] = useState(1);
  const adsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/ads/accepted/by/reporters`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched ads accepted by reporter :", response.data);
        // Handle the new API response structure
        if (response.data && response.data.success) {
          setAds(response.data.data || []);
        } else if (response.data && response.data.message === "No ads found") {
          // Handle "No ads found" response specifically
          setAds([]);
          console.log("No ads found - this is expected when no reporters have accepted ads yet");
        } else {
          setAds([]);
        }
      } catch (err) {
        console.error("Error fetching ads:", err);
        if (err.response && err.response.data && err.response.data.message === "No ads found") {
          // Handle "No ads found" as a normal case, not an error
          setAds([]);
          setError(null);
        } else {
          setError("Failed to fetch ads");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, []);

  // ...existing code...

  // Handle page change for ads list
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );

  if (error)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );

  // Filter logic for search bar
  const filteredAds = ads.filter((ad) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    if (
      (ad.adId && ad.adId.toString().toLowerCase().includes(term)) ||
      (ad._id && ad._id.toString().toLowerCase().includes(term)) ||
      (ad.adTitle && ad.adTitle.toLowerCase().includes(term))
    ) {
      return true;
    }
    // Search within accepted reporters
    if (ad.acceptedReporters && ad.acceptedReporters.some(reporter =>
      (reporter.iinsafId && reporter.iinsafId.toString().toLowerCase().includes(term))
    )) {
      return true;
    }
    return false;
  });

  // Calculate total pages for ads list
  const totalPages = Math.ceil(filteredAds.length / adsPerPage);

  return (
    <>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-700 rounded-2xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-2 flex items-center gap-3">
                <FaCheckCircle className="text-green-300" />
                Accepted By Reporter Ads
              </h1>
              <p className="text-green-100 text-lg">
                Monitor advertisements that have been accepted by reporters and influencers
              </p>
              <div className="mt-4 flex items-center gap-4">
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-green-100">Total Accepted Ads</span>
                  <div className="text-2xl font-bold text-white">{filteredAds.length}</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-green-100">This Page</span>
                  <div className="text-2xl font-bold text-white">{Math.min(adsPerPage, filteredAds.length)}</div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/pendingposts")}
              >
                <FaClock className="w-4 h-4" />
                Pending
              </button>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/runningposts")}
              >
                <FaEye className="w-4 h-4" />
                Running
              </button>
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/approvedposts")}
              >
                <FaCheck className="w-4 h-4" />
                Approved
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/rejectedposts")}
              >
                <FaTimes className="w-4 h-4" />
                Rejected
              </button>
              <button
                className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/completedposts")}
              >
                <FaCheckCircle className="w-4 h-4" />
                Completed
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white"
                placeholder="Search by Ad ID, Ad Title, IINSAF ID..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-green-50 rounded-lg px-4 py-2">
              <span className="text-sm text-green-600 font-medium">Page {currentPage} of {totalPages}</span>
            </div>
            <div className="bg-blue-50 rounded-lg px-4 py-2">
              <span className="text-sm text-blue-600 font-medium">{filteredAds.length} Results</span>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              <FaDownload className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      {filteredAds.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
            <FaCheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Accepted Advertisements
          </h3>
          <p className="text-gray-500 text-lg mb-6">
            {searchTerm ?
              `No advertisements match your search criteria "${searchTerm}".` :
              "No reporters have accepted any advertisements yet. Check back later or approve more ads for reporters to accept."
            }
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Refresh
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {[...filteredAds].reverse()
            .slice((currentPage - 1) * adsPerPage, currentPage * adsPerPage)
            .map((ad) => {
              const adKey = ad._id || ad.adId;
              const paginatedReporters = ad.acceptedReporters || [];
              return (
                <div key={adKey} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  {/* Ad Header */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="bg-green-500 text-white rounded-lg px-3 py-2 font-bold text-lg">
                          {ad.adId}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-semibold">Accepted:</span> {ad.acceptedCount} / {ad.requiredReporter}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-semibold">Total Reporters:</span> {ad.totalReporters}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          <FaCheckCircle className="w-3 h-3 inline mr-1" />
                          Accepted
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Table Content */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            <FaUser className="inline w-4 h-4 mr-2" />
                            IINSAF ID
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            <FaFileAlt className="inline w-4 h-4 mr-2" />
                            Description
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            <FaCheck className="inline w-4 h-4 mr-2" />
                            Accepted
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            <FaFileAlt className="inline w-4 h-4 mr-2" />
                            Ad Proof
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            <FaClock className="inline w-4 h-4 mr-2" />
                            Accepted At
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedReporters && paginatedReporters.length > 0 ? (
                          paginatedReporters.map((reporter, index) => (
                            <tr key={reporter._id || index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <div className="bg-green-100 text-green-800 rounded-lg px-3 py-1 font-mono text-sm">
                                    {reporter.iinsafId || "N/A"}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                                {ad.adTitle || "N/A"}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${reporter.accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                  {reporter.accepted ? (
                                    <>
                                      <FaCheck className="w-3 h-3 mr-1" />
                                      Yes
                                    </>
                                  ) : (
                                    <>
                                      <FaTimes className="w-3 h-3 mr-1" />
                                      No
                                    </>
                                  )}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${reporter.adProof ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                  {reporter.adProof ? (
                                    <>
                                      <FaCheckCircle className="w-3 h-3 mr-1" />
                                      Submitted
                                    </>
                                  ) : (
                                    <>
                                      <FaExclamationTriangle className="w-3 h-3 mr-1" />
                                      Pending
                                    </>
                                  )}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {reporter.acceptedAt ? (
                                  <div>
                                    <div className="font-medium">{new Date(reporter.acceptedAt).toLocaleDateString()}</div>
                                    <div className="text-gray-400">{new Date(reporter.acceptedAt).toLocaleTimeString()}</div>
                                  </div>
                                ) : "N/A"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="px-6 py-12 text-center">
                              <div className="flex flex-col items-center gap-3">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                  <FaUsers className="w-8 h-8 text-gray-400" />
                                </div>
                                <div>
                                  <h4 className="text-lg font-medium text-gray-700">No Accepted Reporters</h4>
                                  <p className="text-gray-500 text-sm">No reporters have accepted this advertisement yet</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <nav className="inline-flex items-center space-x-2 bg-white rounded-2xl shadow-lg p-2 border border-gray-100">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-green-600 hover:bg-green-50 hover:text-green-700'
                }`}
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx + 1}
                onClick={() => handlePageChange(idx + 1)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === idx + 1
                  ? 'bg-green-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                  }`}
                aria-current={currentPage === idx + 1 ? 'page' : undefined}
              >
                {idx + 1}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-green-600 hover:bg-green-50 hover:text-green-700'
                }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </>
  );
};

export default AcceptedByReporterPost;
